import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Loader2, Search } from 'lucide-react'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { showApiError, showSuccess } from '@/lib/utils/notifications'
import { getCompaniesForAddProperty } from '@/features/property/services/propertyService'
import { fetchMatchingProperties, updateMatchingPropertyMode } from '../services/yardiMatchingService'
import { MatchStatusBadge } from '../components/MatchStatusBadge'
import { ModeSelect } from '../components/ModeSelect'
import { SortableHeader } from '../components/SortableHeader'
import { nextSortState, sortRows, type SortDir } from '../utils/tableSort'
import type { MatchingAutomationMode, MatchingPropertyRow } from '../types'
import type { Option } from '@/features/property/types'

function formatLastRun(value?: string | null): string {
  if (!value) return '—'
  const date = new Date(value.includes('T') ? value : value.replace(' ', 'T'))
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

function runStatusLabel(row: MatchingPropertyRow): string {
  if (!row.last_run_at && !row.last_run_id) return 'NOT_SCHEDULED'
  return row.last_status || 'COMPLETED'
}

function propertySortValue(row: MatchingPropertyRow, field: string): string | number {
  if (field === 'property_name') return row.property_name || ''
  if (field === 'yardi_code') return row.yardi_code || ''
  if (field === 'company_name') return row.company_name || ''
  if (field === 'mode') return row.matching_automation_mode || ''
  if (field === 'last_run') {
    if (!row.last_run_at) return 0
    const time = new Date(row.last_run_at.includes('T') ? row.last_run_at : row.last_run_at.replace(' ', 'T')).getTime()
    return Number.isNaN(time) ? 0 : time
  }
  if (field === 'status') return runStatusLabel(row)
  return ''
}

const FIELD =
  'mt-1 h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-800 shadow-sm focus:border-[rgb(21,151,255)] focus:outline-none focus:ring-1 focus:ring-[rgb(21,151,255)]'

export function YardiMatchingConfigPage() {
  const [rows, setRows] = useState<MatchingPropertyRow[]>([])
  const [companies, setCompanies] = useState<Option[]>([])
  const [companyId, setCompanyId] = useState('')
  const [mode, setMode] = useState('')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [sortKey, setSortKey] = useState('property_name')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const searchTimer = useRef<number | null>(null)

  const visibleRows = useMemo(
    () => sortRows(rows, (row) => propertySortValue(row, sortKey), sortDir),
    [rows, sortKey, sortDir],
  )

  const changeSort = (field: string) => {
    const next = nextSortState(sortKey, sortDir, field)
    setSortKey(next.key)
    setSortDir(next.dir)
  }

  const load = async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const data = await fetchMatchingProperties({
        company_id: companyId || undefined,
        mode: mode || undefined,
        search: search || undefined,
      })
      setRows(data)
    } catch (error) {
      setRows([])
      setLoadError('Could not load Yardi properties. Refresh the page or try again.')
      showApiError(error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void getCompaniesForAddProperty()
      .then(setCompanies)
      .catch(() => setCompanies([]))
  }, [])

  useEffect(() => {
    if (searchTimer.current) window.clearTimeout(searchTimer.current)
    const delay = search ? 350 : 0
    searchTimer.current = window.setTimeout(() => {
      void load()
    }, delay)
    return () => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current)
    }
  }, [companyId, mode, search])

  const saveMode = async (row: MatchingPropertyRow, nextMode: MatchingAutomationMode) => {
    setSavingId(row.yardi_property_id)
    try {
      await updateMatchingPropertyMode(row.yardi_property_id, nextMode)
      setRows((current) =>
        current.map((item) =>
          item.yardi_property_id === row.yardi_property_id
            ? { ...item, matching_automation_mode: nextMode }
            : item,
        ),
      )
      showSuccess(`Updated ${row.property_name} to ${nextMode.toLowerCase()}.`)
    } catch (error) {
      showApiError(error)
    } finally {
      setSavingId(null)
    }
  }

  return (
    <div className="min-h-full bg-[#f5f7fb]" data-testid="yardi-matching-config-page">
      <PageHeader
        title="Property Configuration (Automation Settings)"
        testIdRoot="yardi-matching-config-header"
      />

      <div className="space-y-5 p-4 sm:p-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">
            Yardi Resident Matching Automation
          </h2>
          <p className="mt-1 text-sm text-slate-500">
            Scheduled cron runs only Enabled properties. It uses the same Current and Past
            Yardi sync as property detail, auto-maps Node AUTO_MATCH residents, and leaves
            everything else for manual review.
          </p>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <label className="text-sm font-medium text-slate-600">
              Search property
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-3.5 h-4 w-4 text-slate-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') void load()
                  }}
                  placeholder="Search property"
                  className={`${FIELD} pl-9`}
                />
              </div>
            </label>
            <label className="text-sm font-medium text-slate-600">
              Company
              <select value={companyId} onChange={(event) => setCompanyId(event.target.value)} className={FIELD}>
                <option value="">All companies</option>
                {companies.map((company) => (
                  <option key={company.value} value={company.value}>
                    {company.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm font-medium text-slate-600">
              Mode
              <select value={mode} onChange={(event) => setMode(event.target.value)} className={FIELD}>
                <option value="">All modes</option>
                <option value="ENABLED">Enabled</option>
                <option value="SHADOW">Shadow</option>
                <option value="DISABLED">Disabled</option>
              </select>
            </label>
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-[#f8fafc] text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">S.No</th>
                  <SortableHeader label="Property Name" field="property_name" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="Yardi Code" field="yardi_code" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="Company" field="company_name" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="Mode" field="mode" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="Last Run" field="last_run" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <SortableHeader label="Status" field="status" sortKey={sortKey} sortDir={sortDir} onSort={changeSort} />
                  <th className="px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                      <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                      Loading Yardi properties…
                    </td>
                  </tr>
                ) : loadError ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                      {loadError}
                    </td>
                  </tr>
                ) : visibleRows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-12 text-center text-slate-500">
                      No Yardi properties found. A property needs a Yardi code to appear here.
                    </td>
                  </tr>
                ) : (
                  visibleRows.map((row, index) => (
                    <tr key={row.yardi_property_id} className="bg-white hover:bg-slate-50/80">
                      <td className="px-4 py-3.5 text-slate-500">{index + 1}</td>
                      <td className="px-4 py-3.5 font-medium text-slate-900">{row.property_name}</td>
                      <td className="px-4 py-3.5 text-slate-600">{row.yardi_code}</td>
                      <td className="px-4 py-3.5 text-slate-600">{row.company_name}</td>
                      <td className="px-4 py-3.5">
                        <ModeSelect
                          value={row.matching_automation_mode}
                          disabled={savingId === row.yardi_property_id}
                          onChange={(nextMode) => void saveMode(row, nextMode)}
                        />
                      </td>
                      <td className="px-4 py-3.5 text-slate-600">{formatLastRun(row.last_run_at)}</td>
                      <td className="px-4 py-3.5">
                        <MatchStatusBadge value={runStatusLabel(row)} />
                      </td>
                      <td className="px-4 py-3.5">
                        <Link
                          to={`/yardi-matching/property/${row.property_id}`}
                          className="text-sm font-semibold text-[rgb(21,151,255)] hover:underline"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
